// TODO: Overhaul — delete this file (and its consumers/tests) once the Move endpoints are wired (#383 live-migration).
import Decimal from 'decimal.js-light';
import { Client } from 'openapi-fetch';

import { moveAmountMovedToDate } from '@views/investor/move-and-invest/queries/move-progress.helper';
import { mockDelay as delay, ok, paginate, randomLatencyMs } from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreContractsCreateMoveForm as CreateMoveForm,
  SquirrelMoneyCoreContractsMoveDto as MoveDto,
  SquirrelMoneyContractsEnumerationsMoveStatuses as MoveStatuses,
  SquirrelMoneyApiContractsPaginationPagedList_1SquirrelMoneyCoreContractsMoveDtoSquirrelMoneyCoreVersion_1_0_0_0CultureNeutralPublicKeyTokenNull as PagedMoves,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';

/**
 * Attach the elapsed-time-derived cumulative progress to a move as the real `MoveDto.movedAmount` field the
 * "move underway" panel and "stop moving funds" summary read. The immediate portion moves at once; only the sale
 * portion ramps as it sells, so `movedAmount = immediate + sold`. A settled (non-active) move has moved
 * everything. The immediate/sale split is the mock's own bookkeeping (see {@link MockMove}) and isn't exposed.
 */
function withProgress(move: MockMove, now: number): MoveDto {
  const { immediateAmount = 0, saleAmount, amount = 0, ...rest } = move;
  const active = move.currentStatus === MoveStatuses.Active;
  const sale = saleAmount ?? new Decimal(amount).sub(immediateAmount).toNumber();
  const soldToDate = active ? moveAmountMovedToDate(sale, move.createDateNzt, now) : sale;
  const movedAmount = active ? new Decimal(immediateAmount).add(soldToDate).toNumber() : amount;
  return { ...rest, amount, movedAmount };
}

/**
 * In-memory fixture for the consumer Move endpoints, until the overhaul backend is reachable. Lets the
 * move-and-invest and account-detail screens build the create / "move underway" / "stop moving funds"
 * flows against a realistic backend ahead of the API. Reached only through the flag-gated lazy routes,
 * so it ships in a route-level lazy chunk that prod never fetches (the `accountOverhaul` flag is off).
 * Deleted at wire-up — see the live-migration note in `moves.query.ts`.
 */

const accountName = (id: number): string => `Account ${id}`;

/**
 * A stored move plus the mock's own immediate/sale split, from which it derives the ramping `movedAmount` it
 * exposes on the `MoveDto`. The split is internal bookkeeping (not a consumer field): the seam writes it on at
 * create time from the account's moveable split, and the seed sets it explicitly.
 */
export type MockMove = MoveDto & {
  /** Funds awaiting investment that moved immediately (no secondary-market sale). */
  immediateAmount?: number;
  /** Invested funds put up for sale on the secondary market to fulfil the move. */
  saleAmount?: number;
};

/** Fixture factory for a `MockMove`. */
export function createMoveMock(overrides: Partial<MockMove> = {}): MockMove {
  const fromInvestmentAccountId = overrides.fromInvestmentAccountId ?? 1;
  const toInvestmentAccountId = overrides.toInvestmentAccountId ?? 2;
  return {
    moveId: 1,
    fromInvestmentAccountId,
    fromInvestmentAccountName: accountName(fromInvestmentAccountId),
    toInvestmentAccountId,
    toInvestmentAccountName: accountName(toInvestmentAccountId),
    amount: 5_000,
    createDateNzt: new Date().toISOString(),
    currentStatus: MoveStatuses.Active,
    ...overrides,
  };
}

/**
 * Seed the store with a run of COMPLETED moves — enough history to exercise the "show more" list (more than one
 * default page), roughly half touching account 1 so filtering by account still spans multiple pages. No in-flight
 * ("move underway") move is seeded: a tester who wants to see one creates it with the Move + Invest flow (moving
 * out of a Term Investment), which keeps the account balances consistent with the move (see `applyMove`).
 */
const SEED_COUNT = 14;
function seedMoves(): MockMove[] {
  const moves: MockMove[] = [];
  for (let moveId = 1; moveId <= SEED_COUNT; moveId++) {
    const fromInvestmentAccountId = moveId % 2 === 0 ? 1 : 3; // half originate from account 1
    const toInvestmentAccountId = fromInvestmentAccountId === 1 ? 2 : 1;
    moves.push(
      createMoveMock({
        moveId,
        fromInvestmentAccountId,
        toInvestmentAccountId,
        amount: 1_000 * moveId,
        currentStatus: MoveStatuses.Complete,
      }),
    );
  }
  return moves;
}

interface GetInit {
  params?: {
    path?: { investorEntityId?: number; fromInvestmentAccountId?: number; moveId?: number };
    query?: { anyDirectionInvestmentAccountId?: number; pageIndex?: number; pageSize?: number };
  };
}
interface PostInit {
  params?: { path?: { investorEntityId?: number; moveId?: number } };
  body?: CreateMoveForm;
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving the not-yet-live consumer Move endpoints from an
 * in-memory store, so create → active → cancel transitions behave like the real backend. Composed into the
 * single root-singleton overhaul seam (`createOverhaulMockClient`); it is not provided directly by screens.
 *
 * Per-entity store, keyed by the request path's `investorEntityId` and seeded on first touch, so a create /
 * cancel persists across screens (the client is a root singleton) and handles the Move GET/POST routes. Every
 * call waits a random 500ms–1500ms to mimic fetch latency; pass `delayMs` (e.g. `0`) to make it deterministic
 * in tests.
 */
export function createMovesMockClient(delayMs?: number): Client<accountOverhaulPaths> {
  const wait = () => delay(delayMs ?? randomLatencyMs());
  const stores = new Map<number, MockMove[]>();
  const movesFor = (entityId: number): MockMove[] => {
    let moves = stores.get(entityId);
    if (!moves) {
      moves = seedMoves();
      stores.set(entityId, moves);
    }
    return moves;
  };
  const nextIdIn = (moves: MockMove[]): number => moves.reduce((max, m) => Math.max(max, m.moveId ?? 0), 0) + 1;

  return {
    async GET(path: string, init?: GetInit) {
      await wait();
      const pathParams = init?.params?.path ?? {};
      if (pathParams.investorEntityId === undefined) return Promise.resolve(ok(null));
      const moves = movesFor(pathParams.investorEntityId);

      if (path.endsWith('/moves/active')) {
        const active =
          moves.find(
            m =>
              m.fromInvestmentAccountId === pathParams.fromInvestmentAccountId &&
              m.currentStatus === MoveStatuses.Active,
          ) ?? null;
        if (!active) return Promise.resolve(ok(null));
        // Once the ramp reaches the total, the move completes and drops out of "active" — the panel clears.
        const now = Date.now();
        const progressed = withProgress(active, now);
        if ((progressed.movedAmount ?? 0) >= (active.amount ?? 0)) {
          active.currentStatus = MoveStatuses.Complete;
          return Promise.resolve(ok(null));
        }
        return Promise.resolve(ok(progressed));
      }

      if (path.endsWith('/moves/{moveId}')) {
        const move = moves.find(m => m.moveId === pathParams.moveId) ?? null;
        return Promise.resolve(ok(move ? withProgress(move, Date.now()) : null));
      }

      // List `/moves`, optionally filtered to moves touching one account in either direction, then paged.
      const query = init?.params?.query ?? {};
      const filterId = query.anyDirectionInvestmentAccountId;
      const filtered =
        filterId === undefined
          ? [...moves]
          : moves.filter(m => m.fromInvestmentAccountId === filterId || m.toInvestmentAccountId === filterId);
      return Promise.resolve(ok<PagedMoves>(paginate(filtered, query.pageIndex, query.pageSize)));
    },

    async POST(path: string, init?: PostInit) {
      await wait();
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return Promise.resolve(ok(null));
      const moves = movesFor(entityId);

      if (path.endsWith('/cancel')) {
        const moveId = init?.params?.path?.moveId;
        const move = moves.find(m => m.moveId === moveId);
        if (move) move.currentStatus = MoveStatuses.Cancelled;
        return Promise.resolve(ok(move ?? null));
      }

      // Create `/moves`. The schema `CreateMoveForm` fields are optional + nullable; only forward the ones
      // actually supplied so `createMoveMock`'s defaults (and the derived account names) stay consistent —
      // an explicit `undefined` override would otherwise blank the id while keeping the default name.
      const body = init?.body ?? {};
      const created = createMoveMock({
        moveId: nextIdIn(moves),
        ...(body.fromInvestmentAccountId != null ? { fromInvestmentAccountId: body.fromInvestmentAccountId } : {}),
        ...(body.toInvestmentAccountId != null ? { toInvestmentAccountId: body.toInvestmentAccountId } : {}),
        amount: body.amount ?? 0,
        currentStatus: MoveStatuses.Active,
      });
      moves.push(created);
      return Promise.resolve(ok(created));
    },
    // Cast: this stub implements only the Move GET/POST routes, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
